type OptionDetails = {
  helperText?: string;
  category?: string;
  options: string[];
};

type OptionsKeys = 'amount' | 'frequency' | 'duration';

type Options = {
  [K in OptionsKeys]: OptionDetails;
};

export const serviceDeliveryQuestionId = 110001;
export const serviceSubTypeQuestionId = 110002;
export const secondaryServiceSubTypeQuestionId = 110003;
export const serviceAmountQuestionId = 110004;
export const serviceFrequencyQuestionId = 110005;
export const serviceDurationQuestionId = 110006;

const getOptionKey = (questionId: number): OptionsKeys | undefined => {
  switch (questionId) {
    case serviceAmountQuestionId:
      return 'amount';
    case serviceFrequencyQuestionId:
      return 'frequency';
    case serviceDurationQuestionId:
      return 'duration';
    default:
      return undefined;
  }
};

const getSecondarySubTypeOptions = (
  subTypeOptions: OptionDetails & {
    subCategory: {
      [id: string]: Options | undefined;
    };
  },
  serviceSubType: string,
): string[] => {
  const options = subTypeOptions.options.filter(x => x !== serviceSubType);
  return options.length > 0
    ? ['N/A - No secondary service sub-type response', ...options]
    : options;
};

export const getQuestionOptions = (
  questionId: number,
  serviceDelivery: string | undefined,
  serviceSubType: string | undefined,
):
  | {
      helperText?: string;
      category?: string;
      options: string[];
    }
  | undefined => {
  switch (questionId) {
    case serviceDeliveryQuestionId:
      return {
        options: serviceDeliveryOptions,
      };

    case serviceSubTypeQuestionId:
    case secondaryServiceSubTypeQuestionId: {
      if (!serviceDelivery) return undefined;
      const subTypeOptions = activityOptions[serviceDelivery];
      if (!subTypeOptions) return undefined;
      return {
        helperText: subTypeOptions.helperText,
        category: subTypeOptions.category,
        options:
          questionId === secondaryServiceSubTypeQuestionId && serviceSubType
            ? getSecondarySubTypeOptions(subTypeOptions, serviceSubType)
            : subTypeOptions.options,
      };
    }

    case serviceAmountQuestionId:
    case serviceFrequencyQuestionId:
    case serviceDurationQuestionId: {
      const key = getOptionKey(questionId);
      if (!serviceDelivery || !serviceSubType || !key) return undefined;
      const serviceOptions =
        activityOptions[serviceDelivery]?.subCategory[serviceSubType]?.[key];
      if (!serviceOptions) return undefined;
      return {
        helperText: serviceOptions.helperText,
        category: serviceOptions.category,
        options: serviceOptions.options,
      };
    }
    default:
      return undefined;
  }
};

const moneyOptions: string[] = [
  '< $100',
  '$101-500',
  '$501-1,000',
  '$1,000-5,000',
  '$5,000-10,000',
  '$10,000-25,000',
  '$25,000+',
];

const dayOptions: string[] = [
  '<1 day',
  '1-7 days',
  '7 days - 4 weeks',
  '1-6 months',
  '6-12 months',
  '12+ months',
];

const timeOptions: string[] = [
  '< 10 minutes',
  '10-30 minutes',
  '30 minutes-1.5 hours',
  '1.5-4 hours',
  '4-8 hours',
  '8+ hours',
];

const frequencyOptions: string[] = ['1', '2-4', '5-10', '11+'];

const durationOptions: string[] = [
  '1 day',
  'Multiple days',
  'Multiple weeks',
  'Multiple months',
];

export const serviceDeliveryOptions = [
  'Direct Service',
  'Capacity Development',
  'Policy & Advocacy',
];

interface ActivityOptions {
  [id: string]:
    | (OptionDetails & {
        subCategory: {
          [id: string]: Options | undefined;
        };
      })
    | undefined;
}

export const activityOptions: ActivityOptions = {
  'Direct Service': {
    options: [
      'Distribution of food',
      'Distribution of clothing, shelter, and other non-food basic needs',
      'Grants or scholarships for education, training, or other services',
      'Advertising, public service announcements, or social marketing',
      'Individual supports, training, education',
      'Leaderships and professional development',
      'Exhibits or performances',
      'Conferences or convenings',
    ],
    subCategory: {
      'Distribution of food': {
        amount: {
          category: 'goods',
          helperText:
            'Select the average value of the goods provided each time they are distributed:',
          options: moneyOptions,
        },
        frequency: {
          category: 'goods',
          helperText:
            'Select the average number of goods provided each time they are distributed:',
          options: frequencyOptions,
        },
        duration: {
          category: 'goods',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Distribution of clothing, shelter, and other non-food basic needs': {
        amount: {
          category: 'goods',
          helperText:
            'Select the average value of the goods provided each time they are distributed:',
          options: moneyOptions,
        },
        frequency: {
          category: 'goods',
          helperText:
            'Select the average number of goods provided each time they are distributed:',
          options: frequencyOptions,
        },
        duration: {
          category: 'goods',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Grants or scholarships for education, training, or other services': {
        amount: {
          category: 'grants',
          helperText:
            'Select the average value of the grants or scholarships provided:',
          options: moneyOptions,
        },
        frequency: {
          category: 'grants',
          helperText:
            'Select the average number of grants or scholarships provided to a beneficiary over the program duration:',
          options: frequencyOptions,
        },
        duration: {
          category: 'grants',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Advertising, public service announcements, or social marketing': {
        amount: {
          category: 'awareness',
          helperText: 'Select the average length of the messaging:',
          options: timeOptions,
        },
        frequency: {
          category: 'awareness',
          helperText:
            'Select the average number of times a beneficiary is reached with the messaging:',
          options: frequencyOptions,
        },
        duration: {
          category: 'awareness',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Individual supports, training, education': {
        amount: {
          category: 'services',
          helperText:
            'Select the length of these individual sessions, events, or touch points:',
          options: timeOptions,
        },
        frequency: {
          category: 'services',
          helperText:
            'Select average number of these individual sessions, events, or touch points:',
          options: frequencyOptions,
        },
        duration: {
          category: 'services',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Leaderships and professional development': {
        amount: {
          category: 'services',
          helperText:
            'Select the length of these individual sessions, events, or touch points:',
          options: timeOptions,
        },
        frequency: {
          category: 'services',
          helperText:
            'Select the average number of these individual sessions, events, or touch points:',
          options: frequencyOptions,
        },
        duration: {
          category: 'services',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Exhibits or performances': {
        amount: {
          category: 'services',
          helperText:
            'Select the length of these individual sessions, events, or touch points:',
          options: timeOptions,
        },
        frequency: {
          category: 'services',
          helperText:
            'Select the average number of these individual sessions, events, or touch points:',
          options: frequencyOptions,
        },
        duration: {
          category: 'services',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Conferences or convenings': {
        amount: {
          category: 'services',
          helperText:
            'Select the length of these individual sessions, events, or touch points:',
          options: timeOptions,
        },
        frequency: {
          category: 'services',
          helperText:
            'Select the average number of these individual sessions, events, or touch points:',
          options: frequencyOptions,
        },
        duration: {
          category: 'services',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
    },
  },
  'Capacity Development': {
    options: [
      'Network-building, conferences, and collaborations',
      'Technical assistance, professional and leadership development',
      'Direct stakeholder engagement and education',
      'Presentations, exhibits, and online media',
      'Research and evaluation',
      'Infrastructure, equipment, land development',
      'Product and service development',
      'Program and curriculum development',
    ],
    subCategory: {
      'Network-building, conferences, and collaborations': {
        amount: {
          category: 'services',
          helperText:
            'Select the average length of time each participant cumulatively dedicates to these infrastructure or resource ' +
              'development efforts:',
          options: dayOptions,
        },
        frequency: {
          category: 'services',
          helperText:
            'Select the average number of times a beneficary engages with the program (via sessions, events, or other touch points) ' +
              'over the program duration:',
          options: frequencyOptions,
        },
        duration: {
          category: 'services',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Technical assistance, professional and leadership development': {
        amount: {
          category: 'services',
          helperText:
            'Select the length of these individual sessions, events, or touch points:',
          options: timeOptions,
        },
        frequency: {
          category: 'services',
          helperText:
            'Select the average number of times a beneficary engages with the program (via sessions, events, or other touch points) ' +
              'over the program duration:',
          options: frequencyOptions,
        },
        duration: {
          category: 'services',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Direct stakeholder engagement and education': {
        amount: {
          category: 'services',
          helperText:
            'Select the length of these individual sessions, events, or touch points:',
          options: timeOptions,
        },
        frequency: {
          category: 'services',
          helperText:
            'Select the number of technical assistance or network development sessions, events, or other touch points over the ' +
              'program duration:',
          options: frequencyOptions,
        },
        duration: {
          category: 'services',
          helperText:
            'Select the duration of technical assistance, network building, or other engagement activities:',
          options: durationOptions,
        },
      },
      'Presentations, exhibits, and online media': {
        amount: {
          category: 'services',
          helperText:
            'Select the length of these individual sessions, events, or touch points:',
          options: timeOptions,
        },
        frequency: {
          category: 'services',
          helperText:
            'Select the average number of times a beneficary engages with the program (via sessions, events, or other touch points) ' +
              'over the program duration:',
          options: frequencyOptions,
        },
        duration: {
          category: 'services',
          helperText:
            'Select the average duration a beneficiary is served by, interacts with, or participates in the program:',
          options: durationOptions,
        },
      },
      'Research and evaluation': {
        amount: {
          category: 'research',
          helperText:
            'Select the average length of time cumulatively dedicates to each research project:',
          options: dayOptions,
        },
        frequency: {
          category: 'research',
          helperText: 'Select the number of research projects undertaken:',
          options: frequencyOptions,
        },
        duration: {
          category: 'research',
          helperText: 'Select the duration of research and evaluation efforts:',
          options: durationOptions,
        },
      },
      'Infrastructure, equipment, land development': {
        amount: {
          category: 'resources',
          helperText:
            'Select the average value of the resources developed or infrastructure built:',
          options: moneyOptions,
        },
        frequency: {
          category: 'resources',
          helperText:
            'Select the number of people involved in developing the infrastructure, product, serivce, or curriculum:',
          options: frequencyOptions,
        },
        duration: {
          category: 'resources',
          helperText:
            'Select the duration of infrastructure or resource development efforts:',
          options: durationOptions,
        },
      },
      'Product and service development': {
        amount: {
          category: 'resources',
          helperText:
            'Select the average length of time each participant cumulatively dedicates to these infrastructure or resource ' +
              'development efforts:',
          options: dayOptions,
        },
        frequency: {
          category: 'resources',
          helperText:
            'Select the number of people involved in developing the infrastructure, product, service, or curriculum:',
          options: frequencyOptions,
        },
        duration: {
          category: 'resources',
          helperText:
            'Select the duration of technical assistance, network building, or other engagement activities:',
          options: durationOptions,
        },
      },
      'Program and curriculum development': {
        amount: {
          category: 'resources',
          helperText:
            'Select the average length of time each participant cumulatively dedicates to these infrastructure or resource ' +
              'development efforts:',
          options: dayOptions,
        },
        frequency: {
          category: 'resources',
          helperText:
            'Select the number of people involved in developing the infrastructure, product, service, or curriculum:',
          options: frequencyOptions,
        },
        duration: {
          category: 'resources',
          helperText:
            'Select the duration of technical assistance, network building, or other engagement activities:',
          options: durationOptions,
        },
      },
    },
  },
  'Policy & Advocacy': {
    options: [
      'Advertising, public service announcements, or social marketing',
      'Direct engagement (education, advocacy) with the policy makers',
      'Direct engagement (education, advocacy) with the public',
      'Community organizing and coalition building',
      'Fundraising',
      'Litigation',
    ],
    subCategory: {
      'Advertising, public service announcements, or social marketing': {
        amount: {
          category: 'awareness',
          helperText: 'Select the average length of the messaging:',
          options: timeOptions,
        },
        frequency: {
          category: 'awareness',
          helperText:
            'Select the average number of times a beneficiary is reached with the messaging:',
          options: frequencyOptions,
        },
        duration: {
          category: 'awareness',
          helperText:
            'Select the duration of the policy and advocacy activities provided:',
          options: durationOptions,
        },
      },
      'Direct engagement (education, advocacy) with the policy makers': {
        amount: {
          category: 'advocacy/education',
          helperText:
            'Select the average length of time each participant cumulatively dedicates to these policy and advocacy efforts:',
          options: dayOptions,
        },
        frequency: {
          category: 'advocacy/education',
          helperText:
            'Select the number of people participating in these policy and advocacy activities:',
          options: frequencyOptions,
        },
        duration: {
          category: 'advocacy/education',
          helperText:
            'Select the duration of the policy and advocacy activities provided:',
          options: durationOptions,
        },
      },
      'Direct engagement (education, advocacy) with the public': {
        amount: {
          category: 'advocacy/education',
          helperText:
            'Select the average length of time each participant cumulatively dedicates to these policy and advocacy efforts:',
          options: dayOptions,
        },
        frequency: {
          category: 'advocacy/education',
          helperText:
            'Select the number of people participating in these policy and advocacy activities:',
          options: frequencyOptions,
        },
        duration: {
          category: 'advocacy/education',
          helperText:
            'Select the duration of the policy and advocacy activities provided:',
          options: durationOptions,
        },
      },
      'Community organizing and coalition building': {
        amount: {
          category: 'advocacy/education',
          helperText:
            'Select the average length of time each participant cumulatively dedicates to these policy and advocacy efforts:',
          options: dayOptions,
        },
        frequency: {
          category: 'advocacy/education',
          helperText:
            'Select the number of people participating in these policy and advocacy activities:',
          options: frequencyOptions,
        },
        duration: {
          category: 'advocacy/education',
          helperText:
            'Select the duration of the policy and advocacy activities provided:',
          options: durationOptions,
        },
      },
      Fundraising: {
        amount: {
          category: 'advocacy/education',
          helperText:
            'Select the average length of time each participant cumulatively dedicates to these policy and advocacy efforts:',
          options: dayOptions,
        },
        frequency: {
          category: 'advocacy/education',
          helperText:
            'Select the number of people participating in these policy and advocacy activities:',
          options: frequencyOptions,
        },
        duration: {
          category: 'advocacy/education',
          helperText:
            'Select the duration of the policy and advocacy activities provided:',
          options: durationOptions,
        },
      },
      Litigation: {
        amount: {
          category: 'advocacy/education',
          helperText:
            'Select the average length of time each participant cumulatively dedicates to these policy and advocacy efforts:',
          options: dayOptions,
        },
        frequency: {
          category: 'advocacy/education',
          helperText:
            'Select the number of people participating in these policy and advocacy activities:',
          options: frequencyOptions,
        },
        duration: {
          category: 'advocacy/education',
          helperText:
            'Select the duration of the policy and advocacy activities provided:',
          options: durationOptions,
        },
      },
    },
  },
};
